import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Avatar, Box, Icon, Link, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { DuplicateExpenseCardFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { canReviewExpensable } from 'modules/expenses/lib/expensables/availableActions'
import { isPendingExpensable } from 'modules/expenses/lib/expensables/expensable'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface Props {
  locale: string
  expensable: DuplicateExpenseCardFragment
  linkToExpense?: boolean
  isRejecting: boolean
  onReject: (reason: string) => void
  type: 'duplicate_alerts' | 'category_conflict'
  canOverrideApprovalFlow?: boolean
}

const DuplicateExpensesModalContent = ({
  locale,
  expensable,
  linkToExpense,
  isRejecting,
  onReject,
  type,
  canOverrideApprovalFlow,
}: Props) => {
  const { navigation } = useNavigation()
  const [showConfirmationModal, toggleConfirmationModal] = useState(false)
  const [rejectReason, setRejectReason] = useState(
    type === 'duplicate_alerts'
      ? i18n.t('expenses_v2.expense.duplicate_alerts.modal.reason_placeholder')
      : i18n.t('expenses_v2.expense.category_conflict_alerts.modal.reason_placeholder')
  )
  const { section } = useOutletContext<RouteContextParams>()

  const canReview =
    canReviewExpensable(expensable, section) ||
    (isPendingExpensable(expensable) && canOverrideApprovalFlow)

  const { id, groupId } = expensable
  const expensableUrl = groupId
    ? navigation.expenses.groups.id.expenseId.fullPath({ groupId, expense_id: id })
    : navigation.expenses.list.expenses.expenseId.fullPath({ expense_id: id })

  return (
    <Box
      border={{
        all: {
          color: 'grey300',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{ all: 'abs016' }}
    >
      <Box gap="s12" height="full" paddingY="s12" width="s400">
        <Box flexDirection="row" justifyContent="spaceBetween" paddingX="s16">
          <Box flexDirection="row" gap="s8" alignItems="center">
            {expensable.owner?.access && (
              <Avatar
                size="s24"
                url={buildUrl(expensable.owner.access.avatar)}
                label={expensable.owner.access.fullName}
              />
            )}
            <Text size="200" weight="medium" color="grey1000">
              {expensable.owner?.fullName}
            </Text>
            <Tag showCircle type={tagColor(expensable.status)} leftItem="circle" inline>
              {getStatusLabel(expensable.status)}
            </Tag>
          </Box>
          {linkToExpense && (
            <Link.Href href={expensableUrl} target="_blank">
              {i18n.t('common.view')}
            </Link.Href>
          )}
        </Box>

        <Box flexDirection="row" gap="s16" alignItems="center" paddingX="s16">
          <Box flexDirection="row" gap="s6" alignItems="center">
            <Icon.Small icon="calendar" color="grey800" />
            <Text size="200">{dateBackendDateTimeToUI(expensable.effectiveOn)}</Text>
          </Box>
          <Box flexDirection="row" gap="s6" alignItems="center" maxWidth="s240">
            <Icon.Small icon="contract" color="grey800" />
            <Text size="200">
              {amountWithCurrency(Number(expensable.amount), expensable.currency, locale)}
            </Text>
            <Text size="200" ellipsis>
              {i18n.t('expenses_v2.expense.duplicate_alerts.reimbursable_amount', {
                amount: amountWithCurrency(
                  Number(expensable.reimbursableAmount),
                  expensable.reimbursableCurrency ?? expensable.currency,
                  locale
                ),
              })}
            </Text>
          </Box>
        </Box>
        {!isMyExpensesSection(section) && (
          <Box paddingX="s16">
            <F0Button
              variant="outline"
              label={
                expensable.status === 'in_payroll'
                  ? i18n.t('expenses_v2.expense.duplicate_alerts.modal.in_payroll')
                  : i18n.t('expenses_v2.expense.duplicate_alerts.modal.reject')
              }
              disabled={!canReview || isRejecting}
              onClick={() => toggleConfirmationModal(true)}
            />
          </Box>
        )}
        <Box
          width="full"
          height="full"
          textAlign="center"
          justifyContent="center"
          background="neutralBackgroundSubtlest"
          padding="s16"
          minHeight="s400"
          maxHeight="s600"
        >
          {expensable.preview?.url ? (
            // eslint-disable-next-line factorial/no-raw-html
            <img
              alt={i18n.t('expenses_v2.expense.duplicate_alerts.thumbnail')}
              src={buildUrl(expensable.preview?.url)}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Text.Normal>{i18n.t('expenses_v2.viewer.file_without_preview')}</Text.Normal>
          )}
        </Box>
      </Box>
      {showConfirmationModal && (
        <Modal
          illustration="alert"
          title={i18n.t('expenses_v2.list.actions.modal.title_reject_v2')}
          style={{ position: 'relative', top: '200px' }}
          footer={
            <Box flexDirection="row" gap="s12">
              <F0Button
                variant="outline"
                label={i18n.t('common.cancel')}
                onClick={() => toggleConfirmationModal(false)}
              />
              <F0Button
                label={i18n.t('expenses_v2.list.actions.modal.submit_reject')}
                onClick={() => onReject(rejectReason)}
                disabled={isRejecting}
              />
            </Box>
          }
          onClose={() => toggleConfirmationModal(false)}
        >
          <TextArea
            label={i18n.t('expenses_v2.list.actions.modal.body_reject_v2')}
            placeholder={i18n.t('expenses_v2.list.actions.modal.body_reject_placeholder_v2')}
            value={rejectReason}
            onChange={(value) => setRejectReason(value)}
          />
        </Modal>
      )}
    </Box>
  )
}

export { DuplicateExpensesModalContent }
